import SummaryTable from './SummaryTable';
import useTable from 'src/hooks/useTable';
import { useEffect, useState } from 'react';
import { PaginateQuery } from 'src/api/pagination/dtos';
import { caseDtoToCaseManager } from 'src/careflow/common/common-utils';
import { TABLE_HEAD_CASE_PATIENT } from 'src/careflow/common/table-head';
import { Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import { CaseForm } from './CaseForm';
import { PatientWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { usePatient } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { useSnackbar } from 'notistack';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { translateTableHead } from 'src/careflow/helpers/common';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { CaseService } from 'src/crs/case/service/CaseService';

export default function PatientCase({ patientId, disableActions, title, i18n }: any) {
  const [caseData, setCaseData] = useState<any | undefined | null>(null);
  const [refresh, setRefresh] = useState(false);
  const [add, setOpenAdd] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const authUser = useAuth().getCurrentUser();
  const { configurations, componentsData } = useTenantConfigData();

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: 5,
    defaultOrderBy: 'id',
    initialIndex: 1,
    defaultCurrentPage: 1,
  });

  const [patient] = usePatient(patientId, { map: PatientWrapper });

  const getPaginateQuery = () => {
    if (!configurations || !patientId) return null;
    const paginationQuery: PaginateQuery = { page, limit: rowsPerPage };
    paginationQuery.filter = {
      indexedSubjectFhirRefUri: `${configurations.fhir.baseUrl}/Patient/${patientId}`,
    };
    return paginationQuery;
  };

  const getCaseList = async () => {
    const query = getPaginateQuery();
    if (!query) return;
    const cases = await CaseService.getAll(query);
    if (cases) {
      setCaseData(cases);
    }
  };

  useEffect(() => {
    if (configurations && patientId) {
      getCaseList();
    }
  }, [page, rowsPerPage, patientId, configurations]);

  useEffect(() => {
    if (refresh && configurations && patientId) {
      getCaseList();
      setRefresh(false);
    }
  }, [refresh, configurations, patientId]);

  const handlePageChange = (event: unknown, newPage: number) => {
    onChangePage(event, newPage + 1);
  };

  const handleRefresh = () => {
    setRefresh(true);
  };

  const handleCase = async (data: any) => {
    try {
      const autoAssign = componentsData?.carePlan?.autoAssign;
      const payload = { ...data, ...(autoAssign && { assignedTo: authUser?.email }) };
      const result = await CaseService.create(payload);
      if (!result?.fhirId) {
        enqueueSnackbar('Case creation did not return a valid reference. Please try again.', {
          variant: 'error',
        });
        return;
      }
      handleRefresh();
      enqueueSnackbar('Case Created Succesfully', {
        variant: 'success',
      });
    } catch (error) {
      console.log('DEBUG handleCase data: ', data);
      enqueueSnackbar('There was an error trying to create case. Please try again', {
        variant: 'error',
      });
    }
  };

  const data = caseData?.data ? caseData.data.map((r: any) => caseDtoToCaseManager(r)) : [];

  const actionButtons = disableActions ? (
    <Button
      variant="contained"
      size="small"
      sx={{ height: '36px' }}
      startIcon={<Add />}
      onClick={() => setRefresh(true)}
    >
      Refresh
    </Button>
  ) : (
    <Button
      variant="contained"
      size="small"
      sx={{ height: '36px' }}
      startIcon={<Add />}
      onClick={() => setOpenAdd(true)}
    >
      {i18n('patients.details.cases.titleAddCases', 'crs')}
    </Button>
  );

  const permissions = checkAclValidation({ acls: [crsAcls.CRS.PATIENT.CASES.EDIT] });

  return (
    <>
      <SummaryTable
        title={title}
        columns={translateTableHead(TABLE_HEAD_CASE_PATIENT, i18n, 'crs')}
        data={data}
        page={page}
        count={caseData ? caseData.meta.totalItems : 0}
        rowsPerPage={rowsPerPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={onChangeRowsPerPage}
        handleRefresh={handleRefresh}
        actions={permissions ? actionButtons : null}
      />
      <CaseForm
        patient={patient}
        carePlan={null}
        open={add}
        onClose={() => setOpenAdd(false)}
        handleCase={handleCase}
        existData={data?.length > 0 ? true : false}
      />
    </>
  );
}
